import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Box, Checkbox, Drawer, FormControlLabel, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useSelector } from 'react-redux';
import '../../App.css';
import AppButton from '../../common_components/AppButton';
import CustomLoader from '../../common_components/CustomLoader';
import { NumberInput, TextArea, TextInput } from '../../common_components/FormInputs';
import { toast } from '../../common_components/Toast/ToastUtils';
import clockifyGoalService, { goalErrorMessage, goalFieldErrors, } from '../../services/clockifyGoal.service';
/**
 * Clockify Goal - Add / Edit Goal drawer, laid out like the User drawer (header with close + title + Save, a pink
 * section title and the form on white).
 *
 * Goal Name is the Clockify tag name (matched case-insensitively). Weekly / Monthly are minutes per client; either can be
 * left empty when the task has no such target. A month-only task whose time depends on the client type ticks
 * "Rate differs by client type" and gives an Email and a Non-email rate instead of Weekly / Monthly. The server checks the
 * same rules and the name's uniqueness.
 */
const drawerWidth = 600;
const MAX_NAME_LENGTH = 150;
const MAX_NOTES_LENGTH = 500;
const MAX_WEEKLY_MINUTES = 7 * 24 * 60;
const MAX_MONTHLY_MINUTES = 31 * 24 * 60;
/** A month has 4 reporting weeks (WK 1-4 in Employee Performance), so monthly = weekly x 4. */
const WEEKS_PER_MONTH = 4;
/** Up to 2 decimals, no trailing zeros ("4.5", "18"). */
const formatMinutes = (n) => String(Math.round(n * 100) / 100);
const emptyForm = () => ({
    name: '',
    weekly_minutes_per_client: '',
    monthly_minutes_per_client: '',
    monthly_minutes_by_client_type: null,
    notes: '',
});
const minutesError = (label, raw, max) => {
    const value = raw.trim();
    if (!value)
        return undefined;
    const n = Number(value);
    if (!Number.isFinite(n))
        return `${label} must be a number.`;
    if (n <= 0)
        return `${label} must be greater than 0.`;
    if (n > max)
        return `${label} cannot be more than ${max} minutes.`;
    return undefined;
};
const validate = (form) => {
    const errors = {};
    const name = form.name.trim();
    if (!name)
        errors.name = 'Goal name is required.';
    else if (name.length > MAX_NAME_LENGTH)
        errors.name = `Goal name cannot be longer than ${MAX_NAME_LENGTH} characters.`;
    const byType = form.monthly_minutes_by_client_type;
    if (byType) {
        const email = minutesError('Email client minutes', byType.email, MAX_MONTHLY_MINUTES);
        if (email)
            errors.by_client_type_email = email;
        const nonEmail = minutesError('Non-email client minutes', byType.non_email, MAX_MONTHLY_MINUTES);
        if (nonEmail)
            errors.by_client_type_non_email = nonEmail;
        if (!email && !nonEmail && !byType.email.trim() && !byType.non_email.trim()) {
            errors.by_client_type = 'Enter a rate for at least one client type.';
        }
    }
    else {
        const weekly = minutesError('Weekly minutes per client', form.weekly_minutes_per_client, MAX_WEEKLY_MINUTES);
        if (weekly)
            errors.weekly_minutes_per_client = weekly;
        const monthly = minutesError('Monthly minutes per client', form.monthly_minutes_per_client, MAX_MONTHLY_MINUTES);
        if (monthly)
            errors.monthly_minutes_per_client = monthly;
    }
    if (form.notes.trim().length > MAX_NOTES_LENGTH)
        errors.notes = `Notes cannot be longer than ${MAX_NOTES_LENGTH} characters.`;
    return errors;
};
const ClockifyGoalDrawer = ({ open, goal, onClose, onSaved }) => {
    const token = useSelector((state) => state.auth.token);
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    // A minutes field the person typed (or an existing value that is not weekly x 4) is never overwritten by the other
    // field's auto-fill; a field that was only auto-filled follows the other one.
    const manual = useRef({ weekly_minutes_per_client: false, monthly_minutes_per_client: false });
    useEffect(() => {
        if (!open)
            return;
        setErrors({});
        const weekly = goal?.weekly_minutes_per_client ?? null;
        const monthly = goal?.monthly_minutes_per_client ?? null;
        // Editing: an empty target is empty on purpose (e.g. no weekly figure for a twice-a-month task), and a pair that
        // is not weekly x 4 was set by hand - neither is touched. A consistent pair stays linked.
        const linked = weekly != null && monthly != null && formatMinutes(weekly * WEEKS_PER_MONTH) === formatMinutes(monthly);
        manual.current = goal
            ? { weekly_minutes_per_client: !linked, monthly_minutes_per_client: !linked }
            : { weekly_minutes_per_client: false, monthly_minutes_per_client: false };
        setForm(goal
            ? {
                name: goal.name,
                weekly_minutes_per_client: weekly == null ? '' : String(weekly),
                monthly_minutes_per_client: monthly == null ? '' : String(monthly),
                monthly_minutes_by_client_type: goal.monthly_minutes_by_client_type
                    ? {
                        email: goal.monthly_minutes_by_client_type.email == null ? '' : String(goal.monthly_minutes_by_client_type.email),
                        non_email: goal.monthly_minutes_by_client_type.non_email == null ? '' : String(goal.monthly_minutes_by_client_type.non_email),
                    }
                    : null,
                notes: goal.notes || '',
            }
            : emptyForm());
    }, [open, goal]);
    const setField = (field) => (value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
    /** Weekly -> monthly (x 4) and monthly -> weekly (/ 4), unless the other box was filled by hand. */
    const setMinutes = (field) => (value) => {
        const other = field === 'weekly_minutes_per_client' ? 'monthly_minutes_per_client' : 'weekly_minutes_per_client';
        const trimmed = value.trim();
        manual.current[field] = trimmed !== '';
        setForm((prev) => {
            const next = { ...prev, [field]: value };
            if (!manual.current[other]) {
                const n = Number(trimmed);
                if (!trimmed)
                    next[other] = '';
                else if (Number.isFinite(n))
                    next[other] = formatMinutes(field === 'weekly_minutes_per_client' ? n * WEEKS_PER_MONTH : n / WEEKS_PER_MONTH);
            }
            return next;
        });
        setErrors((prev) => ({ ...prev, [field]: undefined, [other]: undefined }));
    };
    const byType = form.monthly_minutes_by_client_type;
    const toggleByType = (on) => {
        setForm((prev) => ({ ...prev, monthly_minutes_by_client_type: on ? { email: '', non_email: '' } : null }));
        setErrors({});
    };
    const setByTypeRate = (key) => (value) => {
        setForm((prev) => ({
            ...prev,
            monthly_minutes_by_client_type: { ...(prev.monthly_minutes_by_client_type || { email: '', non_email: '' }), [key]: value },
        }));
        setErrors((prev) => ({ ...prev, by_client_type: undefined, [`by_client_type_${key}`]: undefined }));
    };
    const handleSave = async () => {
        if (saving)
            return;
        const found = validate(form);
        setErrors(found);
        if (Object.keys(found).length)
            return;
        setSaving(true);
        try {
            const payload = {
                ...form,
                name: form.name.trim(),
                notes: form.notes.trim(),
                // Client-type rates replace the plain targets.
                ...(form.monthly_minutes_by_client_type ? { weekly_minutes_per_client: '', monthly_minutes_per_client: '' } : {}),
            };
            const res = goal
                ? await clockifyGoalService.updateGoal(goal.id, payload, token || '')
                : await clockifyGoalService.createGoal(payload, token || '');
            if (res?.data?.success) {
                toast.success(res.data.message || (goal ? 'Goal updated successfully.' : 'Goal created successfully.'));
                onSaved();
                onClose();
                return;
            }
            const serverErrors = {};
            goalFieldErrors(res).forEach((e) => { serverErrors[e.field] = e.message; });
            setErrors(serverErrors);
            toast.error(goalErrorMessage(res, 'Could not save the goal.'));
        }
        catch {
            toast.error('Could not save the goal.');
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsx(Drawer, { anchor: "right", className: "user-list-edit", open: open, onClose: onClose, sx: {
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
            },
        }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#ffffff' }, children: [_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px',
                        height: '60px',
                        borderBottom: '1px solid #e9eaeb',
                        backgroundColor: '#ffffff',
                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(IconButton, { onClick: onClose, size: "small", disableRipple: true, sx: { p: 0, '&:hover': { backgroundColor: 'transparent' } }, children: _jsx(CloseIcon, { sx: { fontSize: 20, color: '#71717A' } }) }), _jsx("div", { className: "line" }), _jsx(Typography, { sx: { fontSize: '16px', fontWeight: 500, color: '#18181b', lineHeight: '24px' }, className: "drawer-main-title", children: goal ? 'Edit Goal' : 'Add Goal' })] }), _jsx(AppButton, { variant: "contained", onClick: handleSave, startIcon: _jsx(SaveIcon, {}), disabled: saving, color: "primary", className: "app-common-button", children: saving ? _jsx(CustomLoader, { sx: { color: 'white' }, size: 18 }) : 'Save' })] }), _jsx(Box, { className: "form-input-root gap-responsive", component: "form", onSubmit: (e) => { e.preventDefault(); handleSave(); }, sx: {
                        flex: 1,
                        overflowY: 'auto',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        backgroundColor: '#ffffff',
                    }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '10px' }, children: [_jsx(Typography, { sx: { fontSize: '18px', fontWeight: 600, color: '#ff008e', lineHeight: '28px' }, className: "field-drawer-title", children: "Goal Details" }), _jsx(Box, { sx: { height: '1px', backgroundColor: '#e9eaeb', width: '100%' } })] }), _jsx(TextInput, { label: "Goal Name", required: true, placeholder: "Clockify tag name, e.g. PM CLIENT MEETING PREP", value: form.name, onChange: (e) => setField('name')(e.target.value), autoComplete: "off", error: !!errors.name, errorMessage: errors.name, helperText: "Must match the tag name in Clockify (capital letters do not matter)." }), byType ? (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: [_jsxs(Box, { sx: { display: 'flex', gap: '20px' }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(NumberInput, { label: "Email Client Minutes (Monthly)", placeholder: "e.g. 90", value: byType.email, onChange: (e) => setByTypeRate('email')(e.target.value), autoComplete: "off", error: !!errors.by_client_type_email || !!errors.by_client_type, errorMessage: errors.by_client_type_email, helperText: "Minutes per month, per email client.", min: 0, max: MAX_MONTHLY_MINUTES }) }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(NumberInput, { label: "Non-email Client Minutes (Monthly)", placeholder: "e.g. 30", value: byType.non_email, onChange: (e) => setByTypeRate('non_email')(e.target.value), autoComplete: "off", error: !!errors.by_client_type_non_email || !!errors.by_client_type, errorMessage: errors.by_client_type_non_email, helperText: "Minutes per month, per non-email client.", min: 0, max: MAX_MONTHLY_MINUTES }) })] }), errors.by_client_type && (_jsx("span", { className: "input-error-text", children: errors.by_client_type }))] })) : (_jsxs(Box, { sx: { display: 'flex', gap: '20px' }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(NumberInput, { label: "Weekly Minutes Per Client", placeholder: "e.g. 50", value: form.weekly_minutes_per_client, onChange: (e) => setMinutes('weekly_minutes_per_client')(e.target.value), autoComplete: "off", error: !!errors.weekly_minutes_per_client, errorMessage: errors.weekly_minutes_per_client, helperText: "Optional. Fills Monthly as weekly x 4.", min: 0, max: MAX_WEEKLY_MINUTES }) }), _jsx(Box, { sx: { flex: 1 }, children: _jsx(NumberInput, { label: "Monthly Minutes Per Client", placeholder: "e.g. 200", value: form.monthly_minutes_per_client, onChange: (e) => setMinutes('monthly_minutes_per_client')(e.target.value), autoComplete: "off", error: !!errors.monthly_minutes_per_client, errorMessage: errors.monthly_minutes_per_client, helperText: "Optional. Fills Weekly as monthly / 4.", min: 0, max: MAX_MONTHLY_MINUTES }) })] })), _jsxs(Box, { children: [_jsx(FormControlLabel, { label: "Rate differs by client type (email / non-email)", control: _jsx(Checkbox, { checked: !!byType, onChange: (e) => toggleByType(e.target.checked), sx: { '&.Mui-checked': { color: '#ff008e' } } }), sx: { '& .MuiFormControlLabel-label': { fontSize: '14px', color: '#344054' } } }), _jsx(Typography, { sx: { fontSize: '12px', color: '#667085', ml: '32px' }, children: "For a month-only task. Weekly and Monthly are not used when this is on." })] }), _jsx(TextArea, { label: "Notes", placeholder: "Optional", minRows: 4, value: form.notes, onChange: (e) => setField('notes')(e.target.value), error: !!errors.notes, errorMessage: errors.notes })] }) })] }) }));
};
export default ClockifyGoalDrawer;
